import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Chip, Collapse, Skeleton, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { HOURS_SUMMARY } from "../../../theme/tokens";
import { WeeklyShiftView } from "./HoursSummaryExpandedRow";
import type { HoursSummaryEmployeeDetail } from "./types";
import { getEmploymentTypeLabel } from "./utils";

type FilterTab = "all" | "weeklyOt" | "dailyOt" | "shiftOverrun" | "agency";

type Props = {
	employeeDetails?: HoursSummaryEmployeeDetail[];
	isLoading: boolean;
	isActual: boolean;
	sinceDate: DateTime;
};

const MAX_LIST_HEIGHT = 600;

const GROUP_HEADER_BG = "#E3F2FD";
const GROUP_HEADER_HOVER_BG = "#D0E8FC";

const FILTER_TABS: { key: FilterTab; labelKey: string; actualOnly?: boolean }[] = [
	{ key: "all", labelKey: "hours.summary.filter.all" },
	{ key: "weeklyOt", labelKey: "hours.summary.weekly.ot" },
	{ key: "dailyOt", labelKey: "hours.summary.daily.ot" },
	{ key: "agency", labelKey: "hours.summary.agency.hours" },
	{ key: "shiftOverrun", labelKey: "hours.summary.shift.overrun", actualOnly: true },
];

type RoleGroupData = {
	roleGroup: string;
	employees: HoursSummaryEmployeeDetail[];
	totalExtraPayHours: number;
};

export const HoursSummaryDetails: FC<Props> = ({ employeeDetails, isLoading, isActual, sinceDate }) => {
	const { t } = useTranslation();
	const [filterTab, setFilterTab] = useState<FilterTab>("all");
	const { expandedGroups: expandedEmployees, toggleGroupExpand: toggleEmployeeExpand } = useExpandedGroups();
	const { expandedGroups: expandedRoleGroups, toggleGroupExpand: toggleRoleGroupExpand, setExpandedGroups: setExpandedRoleGroups } = useExpandedGroups();

	// Reset to "all" when switching away from actual while shiftOverrun filter is active
	useEffect(() => {
		if (!isActual && filterTab === "shiftOverrun") {
			setFilterTab("all");
		}
	}, [isActual, filterTab]);

	const filteredEmployees = useMemo(() => {
		if (!employeeDetails) return [];
		return employeeDetails.filter((e) => {
			if (filterTab === "all") return e.extraPayHours > 0 || (isActual && e.shiftOverrunHours !== 0);
			if (filterTab === "weeklyOt") return e.weeklyOtHours > 0;
			if (filterTab === "dailyOt") return e.dailyOtHours > 0;
			if (filterTab === "shiftOverrun") return e.shiftOverrunHours !== 0;
			if (filterTab === "agency") return e.agencyHours > 0;
			return true;
		});
	}, [employeeDetails, filterTab, isActual]);

	const groupedEmployees = useMemo((): RoleGroupData[] => {
		const groupMap = new Map<string, HoursSummaryEmployeeDetail[]>();
		for (const emp of filteredEmployees) {
			const existing = groupMap.get(emp.roleGroup);
			if (existing) {
				existing.push(emp);
			} else {
				groupMap.set(emp.roleGroup, [emp]);
			}
		}
		return Array.from(groupMap.entries()).map(([roleGroup, employees]) => ({
			roleGroup,
			employees,
			totalExtraPayHours: employees.reduce((sum, e) => sum + e.extraPayHours, 0),
		}));
	}, [filteredEmployees]);

	useEffect(() => {
		if (groupedEmployees.length === 1) {
			setExpandedRoleGroups(new Set([groupedEmployees[0].roleGroup]));
		}
	}, [groupedEmployees, setExpandedRoleGroups]);

	if (isLoading || !employeeDetails)
		return (
			<CommonDashboardBox sx={{ width: "100%", mt: 3 }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 1,
						py: 1,
					}}
				>
					{[1, 2, 3].map((n) => (
						<Box
							key={n}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 1.5,
								px: 2,
								py: 1.5,
								border: "1px solid",
								borderColor: "grey.200",
								borderRadius: "8px",
							}}
						>
							<Skeleton variant="circular" width={36} height={36} />
							<Box
								sx={{
									flex: 1,
								}}
							>
								<Skeleton variant="text" width="30%" height={22} />
								<Skeleton variant="text" width="15%" height={16} />
							</Box>
							<Box
								sx={{
									display: "flex",
									gap: 1,
								}}
							>
								<Skeleton variant="rounded" width={70} height={36} />
								<Skeleton variant="rounded" width={70} height={36} />
							</Box>
						</Box>
					))}
				</Box>
			</CommonDashboardBox>
		);

	return (
		<CommonDashboardBox sx={{ width: "100%", mt: 3 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "flex-end",
					mb: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						gap: 0.5,
					}}
				>
					{FILTER_TABS.filter((tab) => !tab.actualOnly || isActual).map((tab) => (
						<Chip
							key={tab.key}
							label={t(tab.labelKey)}
							variant={filterTab === tab.key ? "filled" : "outlined"}
							color={filterTab === tab.key ? "default" : "default"}
							onClick={() => setFilterTab(tab.key)}
							sx={{
								fontWeight: filterTab === tab.key ? 700 : 400,
								bgcolor: filterTab === tab.key ? "grey.300" : undefined,
							}}
						/>
					))}
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 1,
				}}
			>
				{groupedEmployees.length === 0 && (
					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
							textAlign: "center",
							py: 2,
						}}
					>
						{t("hours.summary.no.data")}
					</Typography>
				)}
				{groupedEmployees.map((group) => (
					<Box key={group.roleGroup}>
						<Box
							onClick={() => toggleRoleGroupExpand(group.roleGroup)}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								cursor: "pointer",
								bgcolor: GROUP_HEADER_BG,
								borderRadius: "8px",
								px: 2,
								py: 1.5,
								mb: 0.5,
								"&:hover": { bgcolor: GROUP_HEADER_HOVER_BG },
								transition: "background-color 0.2s",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								<KeyboardArrowDownIcon
									fontSize="small"
									sx={{
										transform: expandedRoleGroups.has(group.roleGroup) ? "rotate(0deg)" : "rotate(-90deg)",
										transition: "transform 0.2s",
									}}
								/>
								<Typography
									variant="subtitle1"
									sx={{
										fontWeight: 700,
									}}
								>
									{group.roleGroup}
								</Typography>
								<Chip
									label={`${group.employees.length} ${t("hours.summary.employees")}`}
									size="small"
									sx={{ bgcolor: "white", fontWeight: 500, fontSize: "0.75rem" }}
								/>
							</Box>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								<Typography
									variant="body2"
									sx={{
										color: "text.secondary",
									}}
								>
									{t("hours.summary.extra.pay.hours")}:
								</Typography>
								<Typography
									variant="subtitle2"
									sx={{
										fontWeight: 700,
									}}
								>
									{Math.round(group.totalExtraPayHours * 10) / 10}h
								</Typography>
							</Box>
						</Box>

						<Collapse in={expandedRoleGroups.has(group.roleGroup)} unmountOnExit>
							{/* Not virtualized: cards expand to variable height, which a fixed-row-height virtual list overlaps onto the next row. List is bounded to one role group. */}
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									pl: 2,
									py: 1,
									maxHeight: MAX_LIST_HEIGHT,
									overflowY: "auto",
									// Cards keep their natural height and the list scrolls; without this,
									// flexbox shrinks them to fit maxHeight and overflow:hidden clips them to slivers.
									"& > *": { flexShrink: 0 },
								}}
							>
								{group.employees.map((emp) => (
									<EmployeeCard
										key={emp.nurseId}
										employee={emp}
										isExpanded={expandedEmployees.has(String(emp.nurseId))}
										onToggle={toggleEmployeeExpand}
										isActual={isActual}
										sinceDate={sinceDate}
									/>
								))}
							</Box>
						</Collapse>
					</Box>
				))}
			</Box>
		</CommonDashboardBox>
	);
};

const EmployeeCard: FC<{
	employee: HoursSummaryEmployeeDetail;
	isExpanded: boolean;
	onToggle: (id: string) => void;
	isActual: boolean;
	sinceDate: DateTime;
}> = memo(({ employee, isExpanded, onToggle, isActual, sinceDate }) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const handleToggle = useCallback(() => onToggle(String(employee.nurseId)), [onToggle, employee.nurseId]);
	return (
		<Box
			sx={{
				bgcolor: "white",
				borderRadius: "8px",
				border: "1px solid",
				borderColor: isExpanded ? "primary.light" : "grey.200",
				boxShadow: isExpanded ? "0px 2px 8px rgba(0,0,0,0.08)" : "none",
				transition: "all 0.2s ease",
				overflow: "hidden",
				"&:hover": {
					borderColor: "primary.light",
					boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
				},
			}}
		>
			<Box
				onClick={handleToggle}
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					cursor: "pointer",
					px: 2,
					py: 1.5,
					"&:hover": { bgcolor: "grey.50" },
					transition: "background-color 0.15s",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
					}}
				>
					<Box
						sx={{
							width: 36,
							height: 36,
							borderRadius: "50%",
							bgcolor: "#E8E5F3",
							color: "primary.main",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontWeight: 700,
							fontSize: "0.85rem",
							flexShrink: 0,
						}}
					>
						{employee.firstName?.[0] ?? ""}
						{employee.lastName?.[0] ?? ""}
					</Box>
					<Box>
						<Typography
							variant="body1"
							sx={{
								fontWeight: 600,
								lineHeight: 1.3,
							}}
						>
							{formatPersonName(employee)}
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Typography
								variant="caption"
								sx={{
									color: "text.secondary",
								}}
							>
								{employee.role}
							</Typography>
							<Chip label={getEmploymentTypeLabel(employee.employmentType, t)} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.7rem" }} />
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
					}}
				>
					{employee.weeklyOtHours > 0 && (
						<MetricPill label={t("hours.summary.weekly.ot")} value={`${Math.round(employee.weeklyOtHours * 10) / 10}h`} color={HOURS_SUMMARY.WEEKLY_OT} />
					)}
					{employee.dailyOtHours > 0 && (
						<MetricPill label={t("hours.summary.daily.ot")} value={`${Math.round(employee.dailyOtHours * 10) / 10}h`} color={HOURS_SUMMARY.DAILY_OT} />
					)}
					{isActual && employee.shiftOverrunHours !== 0 && (
						<MetricPill label={t("hours.summary.shift.overrun")} value={`${Math.round(employee.shiftOverrunHours * 10) / 10}h`} color={HOURS_SUMMARY.OVERRUN} />
					)}
					<MetricPill
						label={t("hours.summary.extra.pay.hours")}
						value={`${Math.round(employee.extraPayHours * 10) / 10}h`}
						color={HOURS_SUMMARY.EXTRA_PAY}
						emphasis
					/>
					<KeyboardArrowDownIcon
						sx={{
							transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
							transition: "transform 0.2s",
							color: "text.secondary",
						}}
					/>
				</Box>
			</Box>
			<Collapse in={isExpanded} unmountOnExit>
				<Box sx={{ bgcolor: "background.paper", px: 2, py: 1.5, borderTop: "1px solid", borderColor: "grey.200" }}>
					<WeeklyShiftView shifts={employee.shifts} isActual={isActual} sinceDate={sinceDate} />
				</Box>
			</Collapse>
		</Box>
	);
});

const MetricPill: FC<{
	label: string;
	value: string;
	color: string;
	emphasis?: boolean;
}> = memo(({ label, value, color, emphasis }) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			px: 1.5,
			py: 0.5,
			borderRadius: "12px",
			bgcolor: `${color}14`,
			minWidth: 70,
		}}
	>
		<Typography variant="caption" sx={{ color, fontSize: "0.65rem", lineHeight: 1.2, fontWeight: 500, whiteSpace: "nowrap" }}>
			{label}
		</Typography>
		<Typography variant="body2" sx={{ color, fontWeight: emphasis ? 800 : 700, fontSize: emphasis ? "1rem" : "0.875rem", lineHeight: 1.3 }}>
			{value}
		</Typography>
	</Box>
));
